import { h } from "preact";
import { useState, useEffect } from "preact/hooks";

import useCertStore from "../../store";
import Verified from "./Verified";
import NotFound from "./NotFound";

import CredentialHeader from "./credential_details/CredentialHeader";
import CredentialShare from "./credential_details/CredentialShare";
import CredentialImage from "./credential_details/CredentialImage";
import CredentialSkills from "./credential_details/CredentialSkills";
import CredentialIssuerInfo from "./credential_details/CredentialIssuerInfo";
import CredentialCriteria from "./credential_details/CredentialCriteria";

const Credential = ({ id, data }) => {
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);

  // Select state and actions separately for stability, as recommended by Zustand.
  const certificate = useCertStore((state) => state.certificate);
  const setCertificate = useCertStore((state) => state.setCertificate);

  useEffect(() => {
    // This effect ensures the data in the store matches the ID in the URL.
    // It runs on initial client-load and on client-side navigation.

    // The initial data from SSR is already in the store thanks to client.js,
    // so this condition is mainly for client-side navigation.
    if (!certificate || certificate.verified.subject_id !== id) {
      fetch(
        ` https://6vqysb58ek.execute-api.us-east-1.amazonaws.com/prod/verify?uuid=${id}`,
      )
        .then((res) => {
          return res.ok ? res.json() : null;
        })
        .then((data) => {
          setCertificate(data.credentialData);
          setIsLoading(false);
        })
        .catch(() => setCertificate(null))
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [id]); // The dependency array is minimal and safe, preventing loops.

  // --- Render Logic ---

  // For the very first render (SSR + hydration), use the `data` prop passed from the server.
  // For all subsequent client-side renders, use the `certificate` from the store.
  const displayData = certificate || data;

  // If we are actively fetching new data on the client, show a loading state.
  if (isLoading) {
    return (
      <div class="flex items-center justify-center h-screen bg-gray-100">
        <div class="text-center">
          <p class="text-xl text-gray-600">Cargando certificado...</p>
        </div>
      </div>
    );
  }

  // If there's no data to display for this ID, show Not Found.
  if (!isLoading && (!displayData || Object.keys(displayData).length === 0)) {
    return <NotFound />;
  }

  const socialMediaShare = (platform) => () => {
    fetch(
      `https://31fd8e4ma4.execute-api.us-east-1.amazonaws.com/prod/analytics/social_network/share?network=${platform}&uuid=${id}`,
    )
      .then((res) => {
        return res.ok ? res.json() : null;
      })
      .then((data) => {
        if (data && data.response) {
          if (platform === "download") {
            const blob = new Blob([data.response], { type: "application/jwt" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `credential-${id}.jwt`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          } else {
            window.open(data.response, "_blank");
          }
        }
      });
  };

  return (
    <main class="p-4 grid max-w-[1140px] mx-auto lg:px-0">
      <CredentialHeader credentialName={displayData.credential.name} />

      <div class="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {/* DERECHA: arriba en móvil, derecha en escritorio */}
        <div class="flex flex-col order-1 md:order-2 gap-4 lg:col-span-2">
          <CredentialShare
            socialMediaShare={socialMediaShare}
            isVerifying={isVerifying}
            onVerify={() => {
              setIsVerifying(true);
              setShowModal(true);
            }}
            credentialId={displayData.credential.id}
          />

          <CredentialImage
            image={displayData.credential.credentialSubject.achievement.image}
            pdf={displayData.credential.credentialSubject.achievement.pdf}
          />

          <CredentialSkills
            skills={displayData.credential.badge.skills}
            className="hidden md:block" // Desktop only
          />
        </div>

        {/* IZQUIERDA: abajo en móvil, izquierda en escritorio */}
        <div class="flex flex-col order-2 md:order-1 gap-4 lg:col-span-1">
          <CredentialIssuerInfo displayData={displayData} />

          <CredentialCriteria criteria={displayData.info.criteria} />

          <CredentialSkills
            skills={displayData.credential.badge.skills}
            className="md:hidden" // Mobile only
          />
        </div>
      </div>
      <Verified
        id={id}
        show={showModal}
        onDownload={socialMediaShare("download")}
        onClose={() => setShowModal(false)}
        onLoaded={() => setIsVerifying(false)}
      />
    </main>
  );
};

export default Credential;
